import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';

export interface FeatureFlag {
  id: string;
  name: string;
  description: string | null;
  enabled: boolean;
  enabledForAll: boolean;
  createdAt: string;
  updatedAt: string;
  groups: Array<{ id: string; code: string; name: string }>;
}

interface FeatureFlagsResponse {
  featureFlags: FeatureFlag[];
}

interface FeatureFlagResponse {
  featureFlag: FeatureFlag;
}

// List all feature flags (admin)
export function useFeatureFlags() {
  return useQuery({
    queryKey: ['feature-flags'],
    queryFn: () => api.get<FeatureFlagsResponse>('/admin/feature-flags'),
    staleTime: 60 * 1000, // 1 minute
  });
}

// Get single feature flag
export function useFeatureFlag(id: string) {
  return useQuery({
    queryKey: ['feature-flags', id],
    queryFn: () => api.get<FeatureFlagResponse>(`/admin/feature-flags/${id}`),
    enabled: !!id,
  });
}

// Create feature flag
export function useCreateFeatureFlag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      id: string;
      name: string;
      description?: string;
      enabled?: boolean;
      enabledForAll?: boolean;
    }) => api.post<FeatureFlagResponse>('/admin/feature-flags', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-flags'] });
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}

// Update feature flag
export function useUpdateFeatureFlag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string;
      name?: string;
      description?: string | null;
      enabled?: boolean;
      enabledForAll?: boolean;
    }) => api.patch<FeatureFlagResponse>(`/admin/feature-flags/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-flags'] });
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}

// Delete feature flag
export function useDeleteFeatureFlag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/admin/feature-flags/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-flags'] });
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}

// Set feature flag group assignments
export function useSetFeatureFlagGroups() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, groupIds }: { id: string; groupIds: string[] }) =>
      api.put(`/admin/feature-flags/${id}/groups`, { groupIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-flags'] });
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}
